import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchBhajanDataRequest, selectCatalog, selectStatus } from '../Features/BhajanSlice';
import { AppDispatch } from '../store/store';
import type { AnyGraphNode } from '../types/graphNodes';
import {
  NodeTypeId,
  findEdgeNode,
  nodeName,
} from '../types/graphNodes';
import { deityIconUrl } from '../bhajan/deityIcon';
import { deityAsciiName, isPlaceholderDeity } from '../bhajan/deityIdentity';
import { Card, EmptyState, cn } from '../ui';

const BABIES_CHANNEL = 'https://www.youtube.com/c/BhajanBabies';

interface BabyCardEntry {
  nodeId: number;
  title: string;
  ragam?: string;
  talam?: string;
  deity?: string;
  deityId?: number;
}

const BhajanBabies: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const catalog = useSelector(selectCatalog);
  const status = useSelector(selectStatus);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchBhajanDataRequest());
  }, [dispatch, status]);

  const allNodes = useMemo<AnyGraphNode[]>(
    () => (catalog?.nodes ?? []),
    [catalog]
  );

  /**
   * Surface only bhajans that have a `bb` edge resolving to a Bhajan Babies
   * node with a non-empty YouTube link. Each entry is built from the bhajan
   * itself, so the heading metadata (ragam / talam / deity) reads naturally.
   */
  const entries = useMemo<BabyCardEntry[]>(() => {
    const out: BabyCardEntry[] = [];
    for (const n of allNodes) {
      if (n.nodeTypeId !== NodeTypeId.BHAJAN) continue;
      // The slim index omits the bb target's ytLink; membership is the presence
      // of a `bb` edge (verified equivalent to the old URL check against prod).
      const hasBaby = (n.edges ?? []).some((e) => e.name === 'bb');
      if (!hasBaby) continue;
      const deityNode = findEdgeNode(n, 'deity', allNodes);
      // Same placeholder rule as every other card surface: "stotram" /
      // "mantram" are bhajan-type sub-classes, not deities. Decided from the
      // node id, so it holds in Telugu/Kannada/… as well as English.
      const deityIsPlaceholder = isPlaceholderDeity(
        deityNode?.nodeId,
        deityAsciiName(deityNode)
      );
      out.push({
        nodeId: n.nodeId,
        title: nodeName(n) ?? `Bhajan ${n.nodeId}`,
        ragam: nodeName(findEdgeNode(n, 'ragam', allNodes)),
        talam: nodeName(findEdgeNode(n, 'talam', allNodes)),
        deity: deityIsPlaceholder ? undefined : nodeName(deityNode),
        deityId: deityIsPlaceholder ? undefined : deityNode?.nodeId,
      });
    }
    out.sort((a, b) => a.title.localeCompare(b.title));
    return out;
  }, [allNodes]);

  const loading = status === 'loading' || (status === 'idle' && !catalog);

  return (
    <main id="main">
      <section
        aria-label="Bhajan Babies"
        className="relative border-b border-light"
        style={{
          background:
            'radial-gradient(ellipse at 30% 0%, rgba(245,158,11,0.10) 0%, transparent 55%), radial-gradient(ellipse at 80% 30%, rgba(99,102,241,0.10) 0%, transparent 55%), var(--bg-1)',
        }}
      >
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12 text-center">
          <h1 className="text-h1 font-display font-semibold mb-2">Bhajan Babies</h1>
          <p className="text-text-secondary mb-4 max-w-2xl mx-auto">
            Bhajan Babies is a YouTube channel that tells the stories of Hindu
            deities in a way that is playful and modern. Every month we share
            engaging and enriching animated cartoon bhajans.
          </p>
          <p className="text-small text-text-tertiary mb-5 max-w-2xl mx-auto">
            Bhajan Babies is only possible through the dedication of our
            animators. To sponsor a full or partial video, you can use a credit
            card at{' '}
            <a href="https://bhajanbabies.com" target="_blank" rel="noopener noreferrer" className="text-accent-gold hover:underline">bhajanbabies.com</a>,
            Zelle to{' '}
            <a href="mailto:DonateBB@TrinityDYC.org" className="text-accent-gold hover:underline">DonateBB@TrinityDYC.org</a>,
            or reach out at{' '}
            <a href="mailto:bhajanbabies@gmail.com" className="text-accent-gold hover:underline">bhajanbabies@gmail.com</a>.
          </p>
          <a
            href={BABIES_CHANNEL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-light bg-bg-2/60 hover:bg-bg-2 transition-colors px-4 py-2 text-small text-text-primary"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path
                fill="#FF0000"
                d="M23 7.6a3 3 0 0 0-2.1-2.1C19.1 5 12 5 12 5s-7.1 0-8.9.5A3 3 0 0 0 1 7.6 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.4a3 3 0 0 0 2.1 2.1c1.8.5 8.9.5 8.9.5s7.1 0 8.9-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 23.5 12 31 31 0 0 0 23 7.6z"
              />
              <path fill="white" d="M9.75 15.5V8.5l6 3.5z" />
            </svg>
            Visit Bhajan Babies on YouTube
          </a>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        {loading ? (
          <div
            role="status"
            aria-live="polite"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="p-5">
                <div className="h-32 rounded-xl bg-bg-3/40 animate-pulse" />
              </Card>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            title="No Bhajan Babies bhajans yet"
            description="Once bhajans are linked to Bhajan Babies videos, they'll show up here."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {entries.map((e) => (
              <Link
                key={e.nodeId}
                to={`/nodes/${e.nodeId}`}
                className="group block focus-visible:outline-none"
              >
                <Card
                  interactive
                  className={cn(
                    'relative w-full h-full p-5 transition-all duration-250 ease-out-quint',
                    'hover:-translate-y-0.5 hover:shadow-3 hover:ring-1 hover:ring-accent-gold/40'
                  )}
                >
                  <span
                    className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full opacity-50 group-hover:opacity-100 transition-opacity"
                    style={{
                      background:
                        'linear-gradient(180deg, var(--accent-gold) 0%, var(--accent-rose) 100%)',
                    }}
                    aria-hidden
                  />

                  <div className="flex items-start justify-between mb-2">
                    <span className="font-mono text-small text-text-tertiary tabular-nums">
                      #{e.nodeId}
                    </span>
                    <img
                      src={deityIconUrl(e.deityId)}
                      alt=""
                      aria-hidden
                      width={32}
                      height={32}
                      loading="lazy"
                      className="rounded-full bg-bg-3/60 object-cover shrink-0"
                      style={{ width: 32, height: 32 }}
                    />
                  </div>

                  <h3 className="font-display font-semibold text-text-primary leading-tight text-h3 mb-2 line-clamp-2">
                    {e.title}
                  </h3>

                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-small">
                    {e.ragam && (
                      <>
                        <dt className="text-text-tertiary italic">Ragam</dt>
                        <dd className="text-text-secondary truncate">{e.ragam}</dd>
                      </>
                    )}
                    {e.talam && (
                      <>
                        <dt className="text-text-tertiary italic">Talam</dt>
                        <dd className="text-text-secondary truncate">{e.talam}</dd>
                      </>
                    )}
                    {e.deity && (
                      <>
                        <dt className="text-text-tertiary italic">Deity</dt>
                        <dd className="text-text-secondary truncate">{e.deity}</dd>
                      </>
                    )}
                  </dl>

                  <div className="mt-4 flex items-center justify-between gap-2 text-small">
                    <span className="text-text-tertiary group-hover:text-accent-gold transition-colors">
                      View bhajan →
                    </span>
                    <span className="inline-flex items-center gap-1 text-text-tertiary">
                      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
                        <path
                          fill="#FF0000"
                          d="M23 7.6a3 3 0 0 0-2.1-2.1C19.1 5 12 5 12 5s-7.1 0-8.9.5A3 3 0 0 0 1 7.6 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.4a3 3 0 0 0 2.1 2.1c1.8.5 8.9.5 8.9.5s7.1 0 8.9-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 23.5 12 31 31 0 0 0 23 7.6z"
                        />
                        <path fill="white" d="M9.75 15.5V8.5l6 3.5z" />
                      </svg>
                      BB video
                    </span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default BhajanBabies;
